import React from 'react'
import { Box, Text, Icon, SimpleGrid } from '@chakra-ui/react'
import { format } from 'date-fns'

import SharedStack from '~components/shared/wrappers/shared-stack'
import { useFormatDate } from '~utils/hooks/use-format-date'
import { PrescriptionRefillRequestFieldsFragment } from '~graphql/generated/graphql'
import { IconType } from 'react-icons'
import {
    FaClock,
    FaPaw,
    FaPills,
    FaUser,
    FaNoteSticky,
    FaCheck,
} from 'react-icons/fa6'

interface Props {
    refillRequest: PrescriptionRefillRequestFieldsFragment
    hideStatus?: boolean
}

export default function RefillRequestSummary({
    refillRequest,
    hideStatus,
}: Props) {
    const { formatDate } = useFormatDate()

    const prescriptionName =
        refillRequest.prescription?.product?.name ||
        refillRequest.prescription?.product_name ||
        refillRequest.prescription?.name ||
        'Prescription'

    return (
        <Box p={4} bg="gray.50" borderRadius="md">
            <SimpleGrid columns={2} spacing={3}>
                <RefillRequestSummaryItem
                    label="Prescription"
                    value={prescriptionName}
                    icon={FaPills}
                />
                {refillRequest.patient?.name && (
                    <RefillRequestSummaryItem
                        label="Patient"
                        value={refillRequest.patient.name}
                        icon={FaPaw}
                    />
                )}
                <RefillRequestSummaryItem
                    label="Client"
                    value={
                        refillRequest.client?.first_name +
                            ' ' +
                            refillRequest.client?.last_name || ''
                    }
                    icon={FaUser}
                />
                <RefillRequestSummaryItem
                    label="Pickup Time"
                    value={
                        refillRequest.pickup_time ? (
                            <>
                                <Text fontWeight="semibold">
                                    {format(refillRequest.pickup_time, 'p')}
                                </Text>
                                <Text
                                    fontSize="xs"
                                    fontWeight="normal"
                                    color="gray.500"
                                >
                                    {formatDate(refillRequest.pickup_time)}
                                </Text>
                            </>
                        ) : (
                            <Text
                                fontWeight="normal"
                                fontStyle="italic"
                                color="gray.500"
                            >
                                At client&apos;s convenience
                            </Text>
                        )
                    }
                    icon={FaClock}
                />

                {refillRequest.notes && (
                    <RefillRequestSummaryItem
                        label="Notes"
                        value={refillRequest.notes}
                        icon={FaNoteSticky}
                    />
                )}

                {!hideStatus && refillRequest.status && (
                    <RefillRequestSummaryItem
                        label="Status"
                        value={refillRequest.status}
                        icon={FaCheck}
                    />
                )}
            </SimpleGrid>
        </Box>
    )
}

function RefillRequestSummaryItem({
    label,
    value,
    icon,
}: {
    label: string
    value: React.ReactNode
    icon: IconType
}) {
    return (
        <SharedStack row spacing={4}>
            <Icon as={icon} color="gray.500" fontSize="xl" />
            <Box>
                <Text fontSize="xs" fontWeight="semibold">
                    {label}
                </Text>
                <Text fontSize="sm" fontWeight="normal">
                    {value}
                </Text>
            </Box>
        </SharedStack>
    )
}
